import React from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import FormContainer from '../../../FormContainer';

import useParticipationForm from '../../../../../forms/loan/participation';
import ParticipationService from '../../../../../services/ParticipationService';
import { useUiContext } from '../../../../../context/UiContext';

export default function AddOrEditParticiaption({
  loanId,
  readOnly,
  refetchLoan,
}) {
  const { showToast, showDialogModal } = useUiContext();

  const { participationId } = useParams();
  const navigate = useNavigate();

  const { data: participation } = useQuery({
    queryKey: ['participation', participationId],
    queryFn: () => ParticipationService.getParticipation(participationId),
    enabled: !!participationId,
  });

  const {
    mutate: createParticipation,
    isPending: isParticipationBeingCreated,
  } = useMutation({
    mutationFn: (formValues) => {
      const processedValues = { ...formValues };
      if (processedValues.entity_type === 'servicer') {
        processedValues.lender_id = null;
      }

      if (processedValues.make_whole === false) {
        processedValues.index_rate = null;
      }

      return ParticipationService.createParticipation({
        formValues: processedValues,
        loanId,
      });
    },
    onSuccess: () => {
      showToast({
        text: 'Participation created',
        type: 'success',
      });
      refetchLoan();
      navigate(`/loans/${loanId}?tab=Participation`);
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const {
    mutate: updateParticipation,
    isPending: isParticipationBeingUpdated,
  } = useMutation({
    mutationFn: (formValues) => {
      const processedValues = { ...formValues };
      if (processedValues.entity_type === 'servicer') {
        processedValues.lender_id = null;
      }

      if (processedValues.make_whole === false) {
        processedValues.index_rate = null;
      }

      return ParticipationService.updateParticipation({
        formValues: { ...processedValues, loan_id: loanId },
        participationId,
      });
    },
    onSuccess: () => {
      showToast({
        text: 'Participation updated',
        type: 'success',
      });
      refetchLoan();
      navigate(`/loans/${loanId}?tab=Participation`);
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const {
    mutate: deleteParticipation,
    isPending: isParticipationBeingDeleted,
  } = useMutation({
    mutationFn: () =>
      ParticipationService.deleteParticipation(participation?.id),
    onSuccess: () => {
      showToast({
        text: 'Participation deleted',
        type: 'success',
      });
      refetchLoan();
      navigate(`/loans/${loanId}?tab=Participation`);
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const participationForm = useParticipationForm(
    participationId ? updateParticipation : createParticipation,
    participation,
    readOnly
  );

  return (
    <div className="relative p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
      {participation && !isParticipationBeingDeleted && !readOnly && (
        <button
          onClick={() => {
            showDialogModal({
              title: 'Delete participation',
              message:
                'Are you sure you want to delete this participation agreement?',
              onConfirm: () => deleteParticipation(),
            });
          }}
          type="button"
          className="absolute right-6 btn dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
        >
          <svg
            className="w-4 h-4 fill-current text-rose-500 shrink-0"
            viewBox="0 0 16 16"
          >
            <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
          </svg>
        </button>
      )}
      <FormContainer
        readOnly={readOnly}
        form={participationForm}
        isVisible
        isSubmitting={
          isParticipationBeingCreated ||
          isParticipationBeingUpdated ||
          isParticipationBeingDeleted
        }
        noBillComId
      />
    </div>
  );
}
